/**
 * Responsive video embed with a fail-soft state (FR-VIDEO-01/06/07).
 *
 * The point of this component is that Game Summary, Live Video and the Team
 * Settings preview render video *identically regardless of provider*: they pass
 * an `eventId`, and the video service answers with whatever the tenant
 * configured. No caller knows whether it is looking at Hudl or YouTube.
 *
 * Failure is a first-class state, never an exception:
 *
 * | Server answer                          | What renders                          |
 * | -------------------------------------- | ------------------------------------- |
 * | 200                                    | 16:9 sandboxed iframe                 |
 * | 403 `PROVIDER_NOT_FANATIC_VISIBLE`     | "not available for your account" card |
 * | 404 `PROVIDER_NOT_CONFIGURED`          | "video is not set up" card            |
 * | 404 `EMBED_NOT_CONFIGURED`             | "no video for this game yet" card     |
 * | anything else / network                | "could not load" card + support link  |
 *
 * `isHiddenWhenUnavailable` collapses the slot entirely instead of showing the
 * card — that is the Game Summary public view's behaviour (AC-VIDEO-003), while
 * Live Video keeps the visible fail-soft card.
 */
import { useEffect, useState } from 'react';

import { useApiClient } from '../app/providers';
import { ApiError } from '../lib/api-error';
import type { VideoEmbedInfo } from '../lib/api-types';

/** Why no player is on screen. Drives the fail-soft copy. */
export type VideoEmbedFailReason =
  'fanatic_restricted' | 'plan_excluded' | 'not_configured' | 'not_available' | 'error';

const FAIL_COPY: Record<VideoEmbedFailReason, { icon: string; message: string }> = {
  fanatic_restricted: {
    icon: '🔒',
    message: 'Video is not available for your account on this team.',
  },
  // 402 TIER_LIMIT_EXCEEDED — the team's plan does not include live video.
  plan_excluded: {
    icon: '📷',
    message: 'Video is not available on this team\u2019s current plan.',
  },
  not_configured: { icon: '📷', message: 'Video is not set up for this team yet.' },
  not_available: { icon: '📷', message: 'Video has not been set up for this game yet.' },
  error: { icon: '⚠', message: 'The video could not be loaded. Please try again.' },
};

/** Map a caught error onto a fail-soft reason. Never surfaces a stack trace. */
export function readEmbedFailure(caught: unknown): VideoEmbedFailReason {
  if (!ApiError.isApiError(caught)) return 'error';
  if (caught.code === 'PROVIDER_NOT_FANATIC_VISIBLE') return 'fanatic_restricted';
  if (caught.status === 402 || caught.code === 'TIER_LIMIT_EXCEEDED') return 'plan_excluded';
  if (caught.code === 'PROVIDER_NOT_CONFIGURED') return 'not_configured';
  if (caught.code === 'EMBED_NOT_CONFIGURED') return 'not_available';
  return 'error';
}

export interface VideoEmbedUnavailableProps {
  reason: VideoEmbedFailReason;
  supportUrl?: string | null;
  onRetry?: () => void;
}

export function VideoEmbedUnavailable({
  reason,
  supportUrl = null,
  onRetry,
}: VideoEmbedUnavailableProps): JSX.Element {
  const copy = FAIL_COPY[reason];
  return (
    <div className="video-embed__unavailable" role="status">
      <span className="video-embed__unavailable-icon" aria-hidden="true">
        {copy.icon}
      </span>
      <p className="video-embed__unavailable-message">{copy.message}</p>
      {reason === 'error' && onRetry ? (
        <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
          Try again
        </button>
      ) : null}
      {reason === 'error' && supportUrl !== null && supportUrl !== '' ? (
        <a
          className="video-embed__support-link"
          href={supportUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Contact support
        </a>
      ) : null}
    </div>
  );
}

export interface VideoEmbedProps {
  /** Event whose embed to show. Ignored when `embed` is supplied. */
  eventId: string;
  /** Accessible name for the player. */
  title?: string;
  /** Render nothing at all when no embed is available (Game Summary fan view). */
  isHiddenWhenUnavailable?: boolean;
  /** Skip the fetch and render this embed (Team Settings live preview). */
  embed?: VideoEmbedInfo | null;
  /** Show the "Coaches only / Visible to fans" caption. Default on. */
  isCaptionShown?: boolean;
  className?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; embed: VideoEmbedInfo }
  | { kind: 'failed'; reason: VideoEmbedFailReason; supportUrl: string | null };

export function VideoEmbed({
  eventId,
  title,
  isHiddenWhenUnavailable = false,
  embed = null,
  isCaptionShown = true,
  className,
}: VideoEmbedProps): JSX.Element | null {
  const apiClient = useApiClient();
  const [state, setState] = useState<LoadState>(
    embed === null ? { kind: 'loading' } : { kind: 'ready', embed }
  );
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (embed !== null) {
      setState({ kind: 'ready', embed });
      return undefined;
    }
    const controller = new AbortController();
    setState({ kind: 'loading' });
    apiClient
      .getVideoEmbed(eventId, controller.signal)
      .then((loaded) => {
        // A malformed or empty payload is a fail-soft case, not a crash: the
        // slot must never take a page down.
        if (typeof loaded?.embed_url !== 'string' || loaded.embed_url === '') {
          setState({ kind: 'failed', reason: 'not_available', supportUrl: null });
          return;
        }
        setState({ kind: 'ready', embed: loaded });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          kind: 'failed',
          reason: readEmbedFailure(caught),
          supportUrl: null,
        });
      });
    return () => controller.abort();
  }, [apiClient, embed, eventId, reloadToken]);

  if (state.kind === 'failed' && isHiddenWhenUnavailable) return null;

  const rootClass = ['video-embed', className].filter(Boolean).join(' ');

  if (state.kind === 'loading') {
    return (
      <div className={rootClass}>
        <div className="video-embed__frame video-embed__frame--loading" role="status">
          <span className="video-embed__loading-text">Loading video…</span>
        </div>
      </div>
    );
  }

  if (state.kind === 'failed') {
    return (
      <div className={rootClass}>
        <div className="video-embed__frame">
          <VideoEmbedUnavailable
            reason={state.reason}
            supportUrl={state.supportUrl}
            onRetry={() => setReloadToken((token) => token + 1)}
          />
        </div>
      </div>
    );
  }

  const loaded = state.embed;
  return (
    <figure className={rootClass}>
      <div className="video-embed__frame">
        <iframe
          className="video-embed__iframe"
          src={loaded.embed_url}
          title={title ?? `${loaded.provider_label} video`}
          allow="accelerometer; encrypted-media; picture-in-picture; fullscreen"
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
          allowFullScreen
        />
      </div>
      {isCaptionShown ? (
        <figcaption className="video-embed__caption">
          <span className="video-embed__provider">{loaded.provider_label}</span>
          <span
            className={`badge ${loaded.fanatic_visible ? 'badge--fan-visible' : 'badge--coaches-only'}`}
          >
            {loaded.fanatic_visible ? '✓ Visible to fans' : '🔒 Coaches only'}
          </span>
        </figcaption>
      ) : null}
    </figure>
  );
}
